import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, type BillingSnapshot, type DailyMetric, type Me, type Order, type Printer, type ReconciliationRow } from "./api";
import { openOrderStream } from "./ws";
import { OrderRow } from "./OrderRow";
import { NEXT_STATUS } from "./orderStatus";
import { ToastContainer, type ToastMessage } from "./Toast";
import { playOrderSound } from "./sounds";
import { useTranslation } from "./i18n";
import { SettingsPage } from "./SettingsPage";
import { MenuPage } from "./MenuPage";
import { HelpPage } from "./HelpPage";
import { printOrder } from "./print";
import { OnboardingWizard } from "./OnboardingWizard";
import { AnalyticsPage } from "./AnalyticsPage";
import { RefundPanel } from "./RefundPanel";

type Props = { me: Me };

const PLATFORMS = ["all", "wolt", "uber_eats", "glovo", "pyszne", "bolt_food"] as const;
const STATUS_FILTERS = ["all", "received", "accepted", "preparing", "ready", "picked_up", "delivered", "cancelled"] as const;
// Which backend credential field receives the secret entered in the form.
const SECRET_FIELD: Record<string, "webhook_secret" | "api_key"> = {
  wolt: "webhook_secret",
  uber_eats: "webhook_secret",
  glovo: "webhook_secret",
  pyszne: "api_key",
  bolt_food: "webhook_secret",
};

type Tab = "orders" | "refunds" | "analytics" | "integrations" | "menu" | "settings" | "help";

export function Dashboard({ me }: Props) {
  const { t } = useTranslation();
  const restaurantId = useMemo(
    () => me.memberships[0]?.restaurant_id ?? null,
    [me.memberships],
  );
  const [orders, setOrders] = useState<Order[]>([]);
  const [daily, setDaily] = useState<DailyMetric[]>([]);
  const [reconciliation, setReconciliation] = useState<ReconciliationRow[]>([]);
  const [billing, setBilling] = useState<BillingSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const orderIdsRef = useRef<Set<string>>(new Set());
  const [hiddenOrders, setHiddenOrders] = useState<Set<string>>(() => new Set(JSON.parse(localStorage.getItem("restify.hiddenOrders") || "[]")));
  const [printers, setPrinters] = useState<Printer[]>([]);
  const printersRef = useRef<Printer[]>([]);
  useEffect(() => { printersRef.current = printers; }, [printers]);
  const transitioningRef = useRef<Set<string>>(new Set());
  const [transitioningIds, setTransitioningIds] = useState<Set<string>>(new Set());
  const [kitchenMode, setKitchenMode] = useState(() => localStorage.getItem("restify.kitchenMode") === "1");
  useEffect(() => {
    document.body.classList.toggle("kitchen-mode", kitchenMode);
    localStorage.setItem("restify.kitchenMode", kitchenMode ? "1" : "0");
  }, [kitchenMode]);

  const addToast = useCallback((t: Omit<ToastMessage, "id">) => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev.slice(-4), { ...t, id }]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const startCheckout = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const { url } = await api.checkoutBilling(restaurantId, "growth");
      window.location.href = url;
    } catch (err: unknown) {
      addToast({
        title: t("billing.error"),
        body: err instanceof Error ? err.message : String(err),
        type: "error",
      });
    }
  }, [restaurantId, addToast, t]);

  const [activeTab, setActiveTab] = useState<Tab>("orders");
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [extId, setExtId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [chainId, setChainId] = useState("");
  const [isSandbox, setIsSandbox] = useState(false);
  const [integrations, setIntegrations] = useState<any[]>([]);
  const [editingIntegration, setEditingIntegration] = useState<any | null>(null);
  const [showOnboarding, setShowOnboarding] = useState<boolean | null>(null);

  const loadData = useCallback(() => {
    if (!restaurantId) return;
    setLoading(true);
    Promise.all([
      api.listOrders(restaurantId),
      api.daily(restaurantId).catch(() => [] as DailyMetric[]),
      api.listIntegrations(restaurantId).catch(() => [] as any[]),
      api.listPrinters(restaurantId).catch(() => [] as Printer[]),
      api.reconciliation(restaurantId).catch(() => [] as ReconciliationRow[]),
      api.billing(restaurantId).catch(() => null),
    ]).then(([orderData, dailyData, intData, printerData, reconcData, billingData]) => {
      setOrders(orderData);
      setDaily(dailyData);
      setIntegrations(intData);
      setPrinters(printerData);
      setReconciliation(reconcData);
      setBilling(billingData);
      orderIdsRef.current = new Set(orderData.map((o: Order) => o.id));
    }).catch((err: Error) => {
      setError(err.message);
    }).finally(() => {
      setLoading(false);
    });
  }, [restaurantId]);

  // Auto-show onboarding wizard when no integrations exist
  useEffect(() => {
    if (showOnboarding === null && integrations.length === 0 && !loading && restaurantId && !localStorage.getItem("restify.onboardingDismissed")) {
      setShowOnboarding(true);
    }
  }, [showOnboarding, integrations, loading, restaurantId]);

  // Initial data fetch
  useEffect(() => {
    loadData();
  }, [loadData]);

  // WebSocket
  useEffect(() => {
    if (!restaurantId) return;
    const close = openOrderStream(restaurantId, (msg: { order_id?: string; restaurant_id?: string }) => {
      if (!msg.order_id) return;

      api.getOrder(msg.order_id).then((fullOrder: Order) => {
        setOrders((prev) => {
          const idx = prev.findIndex((o) => o.id === fullOrder.id);
          if (idx >= 0) {
            const copy = [...prev];
            copy[idx] = fullOrder;
            return copy;
          }
          return [fullOrder, ...prev];
        });

        if (!orderIdsRef.current.has(fullOrder.id)) {
          orderIdsRef.current.add(fullOrder.id);
          playOrderSound();
          addToast({ title: "🔔 New Order", body: `#${fullOrder.display_code ?? fullOrder.external_order_id}`, type: "info" });

          if (printersRef.current.some((p) => p.is_active && p.auto_print_new)) {
            printOrder(fullOrder);
            api.printOrder(fullOrder.id).catch(() => {});
          }
        }
      }).catch(err => console.error("Failed to fetch order from ws event:", err));
    });
    return close;
  }, [restaurantId, addToast]);

  // Order transition
  const onTransition = useCallback(async (order: Order, targetStatus?: string) => {
    const newStatus = targetStatus ?? NEXT_STATUS[order.status];
    if (!newStatus) return;
    if (transitioningRef.current.has(order.id)) return;
    transitioningRef.current.add(order.id);
    setTransitioningIds(new Set(transitioningRef.current));
    try {
      const updated = await api.transition(order.id, newStatus);
      setOrders((prev) => prev.map((o) => (o.id === order.id ? updated : o)));
      addToast({ title: "✅ Status Updated", body: `#${order.display_code ?? order.external_order_id} → ${newStatus}`, type: "success" });

      const activePrinters = printers.filter((p) => p.is_active);
      if (newStatus === "accepted" && activePrinters.some((p) => p.auto_print_accepted)) {
        printOrder(updated);
        api.printOrder(updated.id).catch(() => {});
      }
      if (newStatus === "rejected" && activePrinters.some((p) => p.auto_print_rejected)) {
        printOrder(updated);
        api.printOrder(updated.id).catch(() => {});
      }
    } catch (err: unknown) {
      addToast({ title: "❌ Error", body: err instanceof Error ? err.message : String(err), type: "error" });
    } finally {
      transitioningRef.current.delete(order.id);
      setTransitioningIds(new Set(transitioningRef.current));
    }
  }, [addToast, printers]);

  const [submitting, setSubmitting] = useState(false);
  const handleConnect = async (e: FormEvent) => {
    e.preventDefault();
    if (!restaurantId || !selectedPlatform) return;
    setSubmitting(true);
    try {
      if (editingIntegration) {
        const nextConfig: Record<string, unknown> = { ...(editingIntegration.config ?? {}) };
        if (chainId.trim()) {
          nextConfig.chain_id = chainId.trim();
        } else {
          delete nextConfig.chain_id;
        }
        const secretPayload: Record<string, unknown> = {};
        if (apiKey.trim()) {
          secretPayload[SECRET_FIELD[selectedPlatform] ?? "api_key"] = apiKey.trim();
        }
        const updated = await api.updateIntegration(editingIntegration.id, {
          external_restaurant_id: extId,
          ...secretPayload,
          is_sandbox: isSandbox,
          config: nextConfig,
        });
        setIntegrations((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
        addToast({ title: t("toast.connected"), body: t("integrations.updated"), type: "success" });
      } else {
        const payload: Record<string, unknown> = {
          restaurant_id: restaurantId,
          platform: selectedPlatform,
          external_restaurant_id: extId,
          status: "active",
          is_sandbox: isSandbox,
        };
        if (apiKey.trim()) {
          payload[SECRET_FIELD[selectedPlatform] ?? "api_key"] = apiKey.trim();
        }
        if (chainId.trim()) payload.config = { chain_id: chainId.trim() };
        const int = await api.createIntegration(payload);
        setIntegrations((prev) => [...prev, int]);
        addToast({ title: t("toast.connected"), body: `${selectedPlatform} integration added`, type: "success" });
      }
      setShowWizard(false);
      setEditingIntegration(null);
      setExtId("");
      setApiKey("");
      setChainId("");
      setIsSandbox(false);
    } catch (err: unknown) {
      addToast({ title: t("toast.error"), body: err instanceof Error ? err.message : String(err), type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteIntegration = async (id: string) => {
    if (!window.confirm(t("integrations.delete_confirm"))) return;
    try {
      await api.deleteIntegration(id);
      setIntegrations((prev) => prev.filter((i) => i.id !== id));
      addToast({ title: t("integrations.deleted"), body: "", type: "success" });
    } catch (err: unknown) {
      addToast({ title: t("toast.error"), body: err instanceof Error ? err.message : String(err), type: "error" });
    }
  };

  const handleEditIntegration = (int: any) => {
    setEditingIntegration(int);
    setSelectedPlatform(int.platform);
    setExtId(int.external_restaurant_id);
    setApiKey(""); // Don't show encrypted key
    setChainId(int.config?.chain_id ?? "");
    setIsSandbox(int.is_sandbox);
    setWizardStep(2);
    setShowWizard(true);
  };

  // Filtered orders
  const filtered = useMemo(() => {
    let result = orders.filter((o) => !hiddenOrders.has(o.id));
    if (platformFilter !== "all") result = result.filter((o) => o.platform === platformFilter);
    if (statusFilter !== "all") result = result.filter((o) => o.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((o) =>
        (o.display_code ?? o.external_order_id).toLowerCase().includes(q) ||
        o.platform.toLowerCase().includes(q) ||
        (o.customer?.first_name ?? "").toLowerCase().includes(q) ||
        (o.customer?.last_name ?? "").toLowerCase().includes(q) ||
        o.items?.some((it) => it.name.toLowerCase().includes(q))
      );
    }
    return result;
  }, [orders, platformFilter, statusFilter, search, hiddenOrders]);

  if (!restaurantId) {
    return <Onboarding me={me} onCreated={() => window.location.reload()} />;
  }

  if (showOnboarding && restaurantId) {
    return (
      <OnboardingWizard
        restaurantId={restaurantId}
        onComplete={() => {
          setShowOnboarding(false);
          localStorage.setItem("restify.onboardingDismissed", "1");
          loadData();
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="card">
        <div className="loading-spinner" />
        <p className="muted" style={{ textAlign: "center" }}>{t("generic.loading")}</p>
      </div>
    );
  }

  const TABS: { key: Tab; label: string; icon: string }[] = [
    { key: "orders", label: t("tab.orders"), icon: "📦" },
    { key: "refunds", label: "Refunds", icon: "💸" },
    { key: "analytics", label: t("tab.analytics"), icon: "📊" },
    { key: "integrations", label: t("tab.integrations"), icon: "🔗" },
    { key: "menu", label: t("tab.menu"), icon: "🍽️" },
    { key: "settings", label: t("tab.settings"), icon: "⚙️" },
    { key: "help", label: t("tab.help"), icon: "❓" },
  ];

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      {error && (
        <div className="card">
          <p className="error">{error}</p>
        </div>
      )}

      {billing && billing.subscription.status === "trialing" && (
        <div className={`billing-banner${billing.trial_expired ? " billing-banner-expired" : ""}`}>
          <div>
            <strong>
              {billing.trial_expired
                ? t("billing.trial_expired")
                : t("billing.trial_days", { days: String(billing.trial_days_left ?? 0) })}
            </strong>{" "}
            <span className="muted">{t("billing.trial_hint")}</span>
          </div>
          <button
            className="btn btn-primary"
            onClick={startCheckout}
            disabled={!billing.checkout_available}
            title={billing.checkout_available ? undefined : t("billing.unavailable")}
          >
            {t("billing.upgrade")}
          </button>
        </div>
      )}

      <div className="tabs">
        {TABS.map(({ key, label, icon }) => (
          <div
            key={key}
            className={`tab ${activeTab === key ? "tab-active" : ""}`}
            onClick={() => setActiveTab(key)}
          >
            <span className="tab-icon">{icon}</span> {label}
          </div>
        ))}
      </div>

      {activeTab === "orders" ? (
        <>
          <AnalyticsSummary metrics={daily} ordersCount={orders.length} />
          {reconciliation.length > 0 && <ReconciliationCard rows={reconciliation} />}

          <div className="card">
            <div className="orders-header">
              <h2>{t("orders.title")}</h2>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div className="orders-count">{filtered.length} {t("generic.orders")}</div>
                <button className="btn btn-secondary-sm" onClick={() => {
                  const toHide = filtered.map((o) => o.id);
                  if (toHide.length > 0) {
                    const newHidden = new Set([...hiddenOrders, ...toHide]);
                    setHiddenOrders(newHidden);
                    localStorage.setItem("restify.hiddenOrders", JSON.stringify(Array.from(newHidden)));
                    addToast({ title: t("orders.clear_completed"), body: "Orders cleared from view", type: "info" });
                  }
                }}>
                  {t("orders.clear_completed")}
                </button>
                <button
                  className={`btn ${kitchenMode ? "btn-primary" : "btn-secondary-sm"}`}
                  style={{ padding: "8px 14px", fontSize: 13 }}
                  onClick={() => setKitchenMode((m) => !m)}
                  title="Toggle kitchen mode"
                >
                  🍳 {kitchenMode ? "Kitchen" : "Normal"}
                </button>
                <button className="btn btn-primary" style={{ padding: "8px 16px", fontSize: 13 }} onClick={() => {
                  loadData();
                  addToast({ title: t("orders.connect"), body: "Data updated", type: "info" });
                }}>
                  {t("orders.connect")}
                </button>
              </div>
            </div>

            {/* Search & Filters */}
            <div className="filter-bar">
              <input
                className="input search-input"
                type="text"
                placeholder={t("orders.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className="filter-chips">
                {PLATFORMS.map((p) => (
                  <button
                    key={p}
                    className={`chip ${platformFilter === p ? "chip-active" : ""}`}
                    onClick={() => setPlatformFilter(p)}
                  >
                    {p === "all" ? t("orders.all_platforms") : p.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </button>
                ))}
              </div>
              <div className="filter-chips">
                {STATUS_FILTERS.map((s) => (
                  <button
                    key={s}
                    className={`chip ${statusFilter === s ? "chip-active" : ""}`}
                    onClick={() => setStatusFilter(s)}
                  >
                    {s === "all" ? t("orders.all_statuses") : s.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📭</div>
                <p className="muted">
                  {orders.length === 0 ? t("orders.empty") : t("orders.empty_filter")}
                </p>
              </div>
            ) : (
              filtered.map((o) => (
                <OrderRow
                  key={o.id}
                  order={o}
                  onTransition={onTransition}
                  isTransitioning={transitioningIds.has(o.id)}
                  onHide={(id) => {
                    const newHidden = new Set(hiddenOrders);
                    newHidden.add(id);
                    setHiddenOrders(newHidden);
                    localStorage.setItem("restify.hiddenOrders", JSON.stringify(Array.from(newHidden)));
                  }}
                />
              ))
            )}
          </div>
        </>
      ) : activeTab === "refunds" ? (
        <div className="card">
          <RefundPanel restaurantId={restaurantId} onToast={addToast} />
        </div>
      ) : activeTab === "analytics" ? (
        <AnalyticsPage restaurantId={restaurantId} />
      ) : activeTab === "integrations" ? (
        <div className="card">
          <div className="orders-header">
            <h2>{t("integrations.title")}</h2>
            <button className="btn btn-primary" style={{ padding: "8px 16px", fontSize: 13 }} onClick={() => { setShowWizard(true); setWizardStep(1); }}>
              {t("integrations.add")}
            </button>
          </div>
          <p className="muted" style={{ marginBottom: 24 }}>{t("integrations.desc")}</p>

          <div className="platform-grid">
            {integrations.map((int) => (
              <div key={int.id} className="platform-card selected" style={{ cursor: "default" }}>
                <span className="platform-icon">
                  {int.platform === "glovo" ? "🟨" : int.platform === "wolt" ? "🟦" : int.platform === "uber_eats" ? "🟩" : "🥡"}
                </span>
                <div style={{ fontWeight: 600, textTransform: "capitalize" }}>{int.platform.replace("_", " ")}</div>
                <div className="muted" style={{ fontSize: 12 }}>ID: {int.external_restaurant_id}</div>
                <div style={{ marginTop: 12, fontSize: 11, color: int.status === "active" ? "#00f2ff" : "#ff4d4d" }}>
                  ● {int.status.toUpperCase()} {int.is_sandbox && <span style={{ color: "#ffaa00", marginLeft: 8 }}>(SANDBOX)</span>}
                </div>
                <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                  <button className="btn btn-secondary-sm" style={{ flex: 1, fontSize: 10 }} onClick={() => handleEditIntegration(int)}>
                    {t("integrations.edit")}
                  </button>
                  <button className="btn btn-danger-sm" style={{ flex: 1, fontSize: 10 }} onClick={() => handleDeleteIntegration(int.id)}>
                    {t("integrations.delete")}
                  </button>
                </div>
                {int.is_sandbox && (
                  <button
                    className="btn btn-primary-sm"
                    style={{ marginTop: 8, width: "100%", fontSize: 10 }}
                    onClick={() => {
                      api.triggerSimulatedOrder(int.id, int.platform)
                        .then(() => addToast({ title: "🚀 Simulation", body: "Mock order sent", type: "info" }))
                        .catch(err => addToast({ title: "❌ Failed", body: err.message, type: "error" }));
                    }}
                  >
                    🚀 SIMULATE ORDER
                  </button>
                )}
              </div>
            ))}
            {integrations.length === 0 && (
              <div className="empty-state" style={{ gridColumn: "1 / -1" }}>
                <p className="muted">{t("integrations.empty")}</p>
              </div>
            )}
          </div>
        </div>
      ) : activeTab === "menu" ? (
        <MenuPage restaurantId={restaurantId} onToast={addToast} />
      ) : activeTab === "settings" ? (
        <SettingsPage me={me} onToast={addToast} printers={printers} onPrintersChange={setPrinters} />
      ) : (
        <HelpPage />
      )}

      {/* Integration Wizard Modal */}
      {showWizard && (
        <div className="modal-overlay" onClick={() => { setShowWizard(false); setEditingIntegration(null); setExtId(""); setApiKey(""); setChainId(""); setIsSandbox(false); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => { setShowWizard(false); setEditingIntegration(null); setExtId(""); setApiKey(""); setChainId(""); setIsSandbox(false); }}>✕</button>

            <div className="wizard-steps">
              <div className={`step-dot ${wizardStep === 1 ? "active" : ""}`} />
              <div className={`step-dot ${wizardStep === 2 ? "active" : ""}`} />
            </div>

            {wizardStep === 1 ? (
              <>
                <h2 style={{ textAlign: "center", marginBottom: 8 }}>{t("integrations.wizard_title")}</h2>
                <p className="muted" style={{ textAlign: "center", marginBottom: 32 }}>{t("integrations.wizard_desc")}</p>
                <div className="platform-grid">
                  {PLATFORMS.filter(p => p !== "all").map(p => (
                    <div
                      key={p}
                      className={`platform-card ${selectedPlatform === p ? "selected" : ""}`}
                      onClick={() => setSelectedPlatform(p)}
                    >
                      <span className="platform-icon">
                        {p === "glovo" ? "🟨" : p === "wolt" ? "🟦" : p === "uber_eats" ? "🟩" : p === "pyszne" ? "🟧" : "🟪"}
                      </span>
                      <div style={{ fontWeight: 600, textTransform: "capitalize" }}>{p.replace("_", " ")}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 32, display: "flex", justifyContent: "flex-end" }}>
                  <button
                    className="btn btn-primary"
                    disabled={!selectedPlatform}
                    onClick={() => setWizardStep(2)}
                  >
                    {t("integrations.continue")}
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={handleConnect}>
                <h2 style={{ textAlign: "center", marginBottom: 8 }}>Setup {selectedPlatform?.replace("_", " ")}</h2>
                <p className="muted" style={{ textAlign: "center", marginBottom: 32 }}>{t("integrations.wizard_creds")}</p>

                <label className="label">{t("integrations.ext_id")}</label>
                <input
                  className="input"
                  placeholder="e.g. 12345"
                  value={extId}
                  onChange={(e) => setExtId(e.target.value)}
                  required
                  style={{ marginBottom: 16 }}
                />

                <label className="label">{t("integrations.api_key")} {editingIntegration && "(optional)"}</label>
                <input
                  className="input"
                  type="password"
                  placeholder="••••••••••••"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  required={!editingIntegration}
                  style={{ marginBottom: 16 }}
                />

                {selectedPlatform === "glovo" && (
                  <>
                    <label className="label">{t("onboarding.chain_id_label")}</label>
                    <input
                      className="input"
                      placeholder={t("onboarding.chain_id_placeholder")}
                      value={chainId}
                      onChange={(e) => setChainId(e.target.value)}
                      style={{ marginBottom: 4 }}
                    />
                    <p className="muted" style={{ fontSize: 12, marginBottom: 16 }}>
                      {t("onboarding.chain_id_hint")}
                    </p>
                  </>
                )}

                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, cursor: "pointer" }} onClick={() => setIsSandbox(!isSandbox)}>
                  <input type="checkbox" checked={isSandbox} onChange={() => {}} />
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{t("integrations.sandbox_mode") || "Sandbox Mode"}</span>
                </div>

                <div style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }}>
                  <button type="button" className="btn btn-secondary" onClick={() => { setWizardStep(1); setEditingIntegration(null); }}>{t("integrations.back")}</button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? t("integrations.connecting") : editingIntegration ? t("menu.save") : t("integrations.complete")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* ───── Analytics ───── */
function AnalyticsSummary({ metrics, ordersCount }: { metrics: DailyMetric[]; ordersCount: number }) {
  const { t } = useTranslation();
  const totals = metrics.reduce(
    (acc, m) => {
      acc.orders += m.orders_count;
      acc.revenue += m.revenue_minor;
      acc.cancelled += m.cancelled_count;
      return acc;
    },
    { orders: 0, revenue: 0, cancelled: 0 },
  );
  const avgOrder = totals.orders > 0 ? totals.revenue / totals.orders : 0;

  return (
    <div className="card">
      <h2>{t("analytics.title")}</h2>
      <div className="metrics-container">
        <Metric label={t("analytics.total")} value={totals.orders.toString()} icon="📦" />
        <Metric label={t("analytics.revenue")} value={`${(totals.revenue / 100).toFixed(2)} PLN`} icon="💰" />
        <Metric label={t("analytics.avg")} value={`${(avgOrder / 100).toFixed(2)} PLN`} icon="📊" />
        <Metric label={t("analytics.cancelled")} value={totals.cancelled.toString()} icon="❌" accent="danger" />
        <Metric label={t("analytics.active")} value={ordersCount.toString()} icon="🔴" accent="live" />
      </div>
    </div>
  );
}

function Onboarding({ me, onCreated }: { me: Me; onCreated: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.createRestaurant({
        name,
        legal_name: legalName,
        tax_id: taxId,
        address,
        phone,
        contact_email: me.email,
      });
      onCreated();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-card-wrapper" style={{ minHeight: "auto", padding: "40px 0" }}>
      <div style={{ width: "100%", maxWidth: 480 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <h2 style={{ fontSize: 24, fontWeight: 600, color: "var(--text-main)" }}>
            Welcome to Restify!
          </h2>
          <p className="muted" style={{ marginTop: 8 }}>
            {t("generic.no_membership")}
          </p>
        </div>

        <div className="card">
          <h3 style={{ marginBottom: 24, fontSize: 18 }}>Create your first restaurant</h3>
          <form onSubmit={handleSubmit}>
            <label className="label">Restaurant Name</label>
            <input
              className="input"
              placeholder="e.g. My Awesome Bistro"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              style={{ marginBottom: 16 }}
            />

            <label className="label">Legal Entity Name</label>
            <input
              className="input"
              placeholder="e.g. Awesome Bistro LLC"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              required
              style={{ marginBottom: 16 }}
            />

            <label className="label">Tax ID / NIP</label>
            <input
              className="input"
              placeholder="1234567890"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              required
              style={{ marginBottom: 16 }}
            />

            <label className="label">Full Address</label>
            <input
              className="input"
              placeholder="Street, City, Postcode"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required
              style={{ marginBottom: 16 }}
            />

            <label className="label">Phone Number</label>
            <input
              className="input"
              placeholder="+48 ..."
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              style={{ marginBottom: 24 }}
            />

            {error && <p className="error" style={{ marginBottom: 16 }}>{error}</p>}

            <button className="btn btn-primary" style={{ width: "100%", padding: "12px" }} disabled={submitting}>
              {submitting ? "Creating..." : "Create Restaurant & Continue"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, icon, accent }: { label: string; value: string; icon: string; accent?: string }) {
  return (
    <div className={`metric-card ${accent ? `metric-${accent}` : ""}`}>
      <div className="metric-icon">{icon}</div>
      <div className="muted" style={{ fontSize: 11, letterSpacing: "0.06em", marginBottom: 4 }}>
        {label.toUpperCase()}
      </div>
      <div style={{ fontSize: 24, fontWeight: 600, color: "var(--text-main)" }}>{value}</div>
    </div>
  );
}

const PLATFORM_ICON: Record<string, string> = {
  wolt: "🟦",
  uber_eats: "🟩",
  glovo: "🟨",
  pyszne: "🟧",
  bolt_food: "🟪",
};

function ReconciliationCard({ rows }: { rows: ReconciliationRow[] }) {
  const fmt = (minor: number) => `${(minor / 100).toFixed(2)}`;
  return (
    <div className="card">
      <h2>💳 Revenue Reconciliation (30 days)</h2>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ color: "var(--text-muted)", textTransform: "uppercase", fontSize: 10, letterSpacing: "0.08em" }}>
              <th style={{ textAlign: "left", padding: "6px 12px 10px 0", fontWeight: 600 }}>Platform</th>
              <th style={{ textAlign: "right", padding: "6px 12px 10px", fontWeight: 600 }}>Orders</th>
              <th style={{ textAlign: "right", padding: "6px 12px 10px", fontWeight: 600 }}>Revenue</th>
              <th style={{ textAlign: "right", padding: "6px 12px 10px", fontWeight: 600 }}>Delivery</th>
              <th style={{ textAlign: "right", padding: "6px 12px 10px", fontWeight: 600 }}>Service</th>
              <th style={{ textAlign: "right", padding: "6px 12px 10px", fontWeight: 600 }}>Tips</th>
              <th style={{ textAlign: "right", padding: "6px 12px 10px", fontWeight: 600 }}>Discounts</th>
              <th style={{ textAlign: "right", padding: "6px 0 10px 12px", fontWeight: 600, color: "var(--accent-primary)" }}>Net</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.platform} style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                <td style={{ padding: "10px 12px 10px 0", display: "flex", alignItems: "center", gap: 8 }}>
                  <span>{PLATFORM_ICON[r.platform] ?? "🔲"}</span>
                  <span style={{ fontWeight: 500, textTransform: "capitalize" }}>{r.platform.replace("_", " ")}</span>
                </td>
                <td style={{ textAlign: "right", padding: "10px 12px", color: "var(--text-muted)" }}>{r.orders_count}</td>
                <td style={{ textAlign: "right", padding: "10px 12px" }}>{fmt(r.revenue_minor)}</td>
                <td style={{ textAlign: "right", padding: "10px 12px", color: "var(--text-muted)" }}>
                  {r.delivery_fee_minor > 0 ? fmt(r.delivery_fee_minor) : "—"}
                </td>
                <td style={{ textAlign: "right", padding: "10px 12px", color: "var(--text-muted)" }}>
                  {r.service_fee_minor > 0 ? fmt(r.service_fee_minor) : "—"}
                </td>
                <td style={{ textAlign: "right", padding: "10px 12px", color: "var(--success)" }}>
                  {r.tip_minor > 0 ? fmt(r.tip_minor) : "—"}
                </td>
                <td style={{ textAlign: "right", padding: "10px 12px", color: "var(--danger)" }}>
                  {r.discount_minor > 0 ? `-${fmt(r.discount_minor)}` : "—"}
                </td>
                <td style={{ textAlign: "right", padding: "10px 0 10px 12px", fontWeight: 700, color: "var(--accent-primary)" }}>
                  {fmt(r.net_minor)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}>
              <td style={{ padding: "10px 12px 0 0", fontWeight: 600, fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Total</td>
              <td style={{ textAlign: "right", padding: "10px 12px 0", fontWeight: 600 }}>
                {rows.reduce((s, r) => s + r.orders_count, 0)}
              </td>
              <td style={{ textAlign: "right", padding: "10px 12px 0", fontWeight: 600 }}>
                {fmt(rows.reduce((s, r) => s + r.revenue_minor, 0))}
              </td>
              <td style={{ textAlign: "right", padding: "10px 12px 0", color: "var(--text-muted)" }}>
                {fmt(rows.reduce((s, r) => s + r.delivery_fee_minor, 0))}
              </td>
              <td style={{ textAlign: "right", padding: "10px 12px 0", color: "var(--text-muted)" }}>
                {fmt(rows.reduce((s, r) => s + r.service_fee_minor, 0))}
              </td>
              <td style={{ textAlign: "right", padding: "10px 12px 0", color: "var(--success)" }}>
                {fmt(rows.reduce((s, r) => s + r.tip_minor, 0))}
              </td>
              <td style={{ textAlign: "right", padding: "10px 12px 0", color: "var(--danger)" }}>
                -{fmt(rows.reduce((s, r) => s + r.discount_minor, 0))}
              </td>
              <td style={{ textAlign: "right", padding: "10px 0 0 12px", fontWeight: 700, color: "var(--accent-primary)", fontSize: 15 }}>
                {fmt(rows.reduce((s, r) => s + r.net_minor, 0))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
